/**
 * File type validation configuration and utilities
 * Centralized location for all allowed file types across the application
 */

export enum AppFileType {
	IMAGE = "image",
	VIDEO = "video",
	AUDIO = "audio"
}

// Allowed file extensions for each file type
export const ALLOWED_EXTENSIONS = {
	[AppFileType.IMAGE]: [".png", ".jpg", ".jpeg", ".gif", ".webp", ".bmp", ".svg"],
	[AppFileType.VIDEO]: [".mp4", ".mov", ".avi", ".webm", ".mkv", ".flv", ".wmv", ".m4v"],
	[AppFileType.AUDIO]: [".mp3", ".wav", ".aac", ".ogg", ".flac", ".m4a", ".wma"]
};

// Allowed MIME types for each file type
export const ALLOWED_MIME_TYPES = {
	[AppFileType.IMAGE]: [
		"image/png", "image/jpeg", "image/jpg", "image/gif", 
		"image/webp", "image/bmp", "image/svg+xml"
	],
	[AppFileType.VIDEO]: [
		"video/mp4", "video/mov", "video/avi", "video/webm", 
		"video/mkv", "video/flv", "video/wmv", "video/m4v"
	],
	[AppFileType.AUDIO]: [
		"audio/mp3", "audio/wav", "audio/aac", "audio/ogg", 
		"audio/flac", "audio/m4a", "audio/wma"
	]
};	

// Combined list of all allowed extensions
export const ALL_EXTENSIONS = Object.values(ALLOWED_EXTENSIONS).flat();

// Combined list of all allowed MIME types
export const ALL_MIME_TYPES = Object.values(ALLOWED_MIME_TYPES).flat();

// File type validation functions
export function validateFileType(filePath: string, fileType: AppFileType): boolean {
	// Get file extension from path
	const extension = filePath.toLowerCase().substring(filePath.lastIndexOf('.'));
	
	// Check if extension is allowed for the specified type
	return ALLOWED_EXTENSIONS[fileType]?.includes(extension) || false;
}

export function validateMimeType(mimeType: string, fileType: AppFileType): boolean {
	// Check if MIME type is allowed for the specified type
	return ALLOWED_MIME_TYPES[fileType]?.includes(mimeType) || false;
}

export function getFileTypeFromMimeType(mimeType: string): AppFileType | null {
	if (mimeType.startsWith("image/")) {
		return AppFileType.IMAGE;
	}
	if (mimeType.startsWith("video/")) {
		return AppFileType.VIDEO;
	}
	if (mimeType.startsWith("audio/")) {
		return AppFileType.AUDIO;
	}
	return null;
}

export function getFileTypeFromExtension(extension: string): AppFileType | null {
	const ext = extension.toLowerCase();
	
	for (const [type, extensions] of Object.entries(ALLOWED_EXTENSIONS)) {
		if (extensions.includes(ext)) {
			return type as AppFileType;
		}
	}
	
	return null;
}

// React Dropzone accept configuration
export const DROPZONE_ACCEPT = {
	"image/*": ALLOWED_EXTENSIONS[AppFileType.IMAGE],
	"video/*": ALLOWED_EXTENSIONS[AppFileType.VIDEO],
	"audio/*": ALLOWED_EXTENSIONS[AppFileType.AUDIO]
} as const;

// Utility functions for file validation
export function isImageFile(file: File): boolean {
	return file.type.startsWith("image/") && validateMimeType(file.type, AppFileType.IMAGE);
}

export function isVideoFile(file: File): boolean {
	return file.type.startsWith("video/") && validateMimeType(file.type, AppFileType.VIDEO);
}

export function isAudioFile(file: File): boolean {
	return file.type.startsWith("audio/") && validateMimeType(file.type, AppFileType.AUDIO);
}

export function isValidMediaFile(file: File): boolean {
	return isImageFile(file) || isVideoFile(file) || isAudioFile(file);
}

// File size formatting utilities
export function formatFileSize(bytes: number): string {
	if (bytes === 0) return "0 B";
	const k = 1024;
	const sizes = ["B", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return `${Number.parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
}

export function formatFileSizeInMB(bytes: number): number {
	return Number.parseFloat((bytes / (1024 * 1024)).toFixed(2));
} 